import type { CalendarAvailability } from './types';

export type NextAvailableStay = {
  checkIn: string;
  checkOut: string;
  nights: number;
};

const AVAILABLE = 'available';

const addDays = (date: string, days: number): string => {
  const parsed = new Date(`${date}T00:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
};

const fitsStayRules = (day: CalendarAvailability, nights: number): boolean => {
  if (day.minimum_stay > nights) {
    return false;
  }

  return day.maximum_stay === null || nights <= day.maximum_stay;
};

const holdsStay = (days: CalendarAvailability[], startIndex: number, nights: number): boolean => {
  if (startIndex + nights > days.length) {
    return false;
  }

  for (let offset = 0; offset < nights; offset++) {
    const day = days[startIndex + offset];

    if (day.type !== AVAILABLE) {
      return false;
    }

    if (day.date !== addDays(days[startIndex].date, offset)) {
      return false;
    }
  }

  return true;
};

/**
 * Earliest check-in on or after `fromDate` whose next `nights` are all bookable.
 *
 * Every night in the run must be `available`; `check_in_closed` and `hotel_closed` can neither
 * start nor continue a stay. The check-in night carries the stay-length rules, so a lone open
 * night inside a closed period is not an answer.
 *
 * Returns null when nothing fits. Callers must then fall back to their generic message rather
 * than state there is no availability — some providers truncate the calendar at 90 days and
 * others return nothing at all, so absence here is not evidence of a full hotel.
 */
export const findNextAvailableStay = (
  availability: CalendarAvailability[],
  fromDate: string,
  nights: number
): NextAvailableStay | null => {
  if (nights < 1) {
    return null;
  }

  const days = (Array.isArray(availability) ? availability : [])
    .filter((day) => day.date >= fromDate)
    .sort((a, b) => a.date.localeCompare(b.date));

  for (let index = 0; index < days.length; index++) {
    if (!fitsStayRules(days[index], nights)) {
      continue;
    }

    if (!holdsStay(days, index, nights)) {
      continue;
    }

    return {
      checkIn: days[index].date,
      checkOut: addDays(days[index].date, nights),
      nights,
    };
  }

  return null;
};
